import { useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { Sheet } from '@/components/shared/Sheet';
import { Chip } from '@/components/shared/Chip';
import { FAQ_ITEMS } from '@/lib/reference/faq';

interface Props {
  onClose: () => void;
}

function FaqAnswer({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className="overflow-hidden">
        <div className="border-t border-line px-4 pt-3.5 pb-4">
          <p className="text-[13px] leading-[1.65] text-ink-soft">{children}</p>
        </div>
      </div>
    </div>
  );
}

export function FAQSheet({ onClose }: Props) {
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState('All');
  const cats = ['All', ...Array.from(new Set(FAQ_ITEMS.map((i) => i.cat)))];
  const filtered = filter === 'All' ? FAQ_ITEMS : FAQ_ITEMS.filter((i) => i.cat === filter);

  return (
    <Sheet title="Frequently Asked" onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        <div className="mb-4 text-[13px] leading-[1.55] text-ink-soft">
          Quick answers to the questions members ask most. Still stuck? Tap &ldquo;Send Feedback&rdquo; on Profile.
        </div>

        <div className="mb-4 flex gap-1.5 overflow-x-auto pb-1">
          {cats.map((c) => (
            <Chip key={c} active={filter === c} onClick={() => setFilter(c)}>
              {c}
            </Chip>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.map((item, i) => {
            const id = `${item.cat}-${i}`;
            const open = openIds[id];
            return (
              <div
                key={id}
                className={`overflow-hidden rounded-[12px] border bg-paper shadow-bt transition-colors duration-200 ${
                  open ? 'border-line' : 'border-transparent'
                }`}
              >
                <button
                  type="button"
                  onClick={() => setOpenIds((o) => ({ ...o, [id]: !o[id] }))}
                  className={`flex w-full items-start gap-3 px-4 text-left ${
                    open ? 'pt-4 pb-3' : 'py-4'
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 text-[10px] font-bold tracking-wide text-gold-deep">
                      {item.cat.toUpperCase()}
                    </div>
                    <div className="font-serif text-[14px] font-semibold leading-[1.35] tracking-[-0.005em] text-ink">
                      {item.q}
                    </div>
                  </div>
                  <ChevronRight
                    size={18}
                    className={`mt-1 shrink-0 text-mute transition-transform duration-300 ease-in-out ${
                      open ? 'rotate-90' : ''
                    }`}
                  />
                </button>
                <FaqAnswer open={!!open}>{item.a}</FaqAnswer>
              </div>
            );
          })}
        </div>

        <div className="mt-6 rounded border-l-2 border-gold bg-gold/[0.08] px-3.5 py-3 text-[10.5px] italic leading-[1.55] text-ink-soft">
          Bag Tag is independently built and operated. Not affiliated with, endorsed by, or sponsored by Army Navy Country Club. ANCC and its related marks are property of Army Navy Country Club; references in this app exist to identify the member community served.
        </div>
      </div>
    </Sheet>
  );
}
